import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as teamMemberActions from '@redux/modules/teamMember'

import defaultFormState, { requiredFields } from '@models/teamMember'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'
import type { TeamMemberModel, TeamMemberRequestOptions } from '@models/types'

type CreateTeamMemberParams = {
  dispatch: AppDispatch,
  requestOptions?: TeamMemberRequestOptions,
  teamMemberParams: Partial<TeamMemberModel>,
}

const createTeamMember = (params: CreateTeamMemberParams) => {
  const { dispatch, teamMemberParams, requestOptions } = params
  const { createTeamMember: createFn } = teamMemberActions

  return dispatch(createFn(teamMemberParams, requestOptions))
}

type UpdateTeamMemberParams = {
  dispatch: AppDispatch,
  requestOptions?: TeamMemberRequestOptions,
  teamMember: TeamMemberModel,
  teamMemberParams: Partial<TeamMemberModel>,
}

const updateTeamMember = (params: UpdateTeamMemberParams) => {
  const {
    dispatch, teamMember, teamMemberParams, requestOptions,
  } = params
  const { updateTeamMember: updateFn } = teamMemberActions

  const updatedParams = {
    id: teamMember.id,
    ...teamMemberParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteTeamMemberParams = {
  dispatch: AppDispatch,
  teamMember: DeleteParams<TeamMemberModel>,
}

const deleteTeamMember = (params: DeleteTeamMemberParams) => {
  const { dispatch, teamMember } = params
  const { deleteTeamMember: deleteFn } = teamMemberActions

  return dispatch(deleteFn(teamMember))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTeamMemberForm(
  teamMember: Partial<TeamMemberModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const teamMemberForm = useForm(
    defaultFormState,
    { entity: teamMember, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [teamMember.id, teamMember.cache_key],
  )

  return {
    ...teamMemberForm,
  }
}

export const useRelations = (teamMember: TeamMemberModel) => {
  const { user_id } = teamMember

  const { images, users } = useSelector(reduxState => reduxState.entities)

  const user = user_id && users[user_id] ? users[user_id] : {}
  // @ts-expect-error TS2339
  const image = user.image_id && images[user.image_id] ? images[user.image_id] : {}

  return {
    image,
    user,
  }
}

function useTeamMember(initEntity: Partial<TeamMemberModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: teamMember }: { entity: TeamMemberModel} = useLatestEntity(initEntity, 'teamMembers')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { image, user } = useRelations(teamMember)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.teamMembers)

  return {
    callbacks: {
      createTeamMember: (teamMemberParams: Partial<TeamMemberModel>, entityOptions?: TeamMemberRequestOptions) => (
        createTeamMember({ teamMemberParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditAdminTeamMember: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAdminTeamMemberModal',
        payload: { teamMember },
      }),
      createOrEditAgencyTeamMember: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAgencyTeamMemberModal',
        payload: { teamMember },
      }),
      deleteTeamMember: () => deleteTeamMember({ dispatch, teamMember }),
      updateTeamMember: (teamMemberParams: Partial<TeamMemberModel>, entityOptions?: TeamMemberRequestOptions) => (
        updateTeamMember({
          teamMember, teamMemberParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    image,
    loading,
    teamMember,
    updating,
    user,
  }
}

export default useTeamMember
